import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  DEFAULT_DEALER_REPORT_RANGE,
  formatPercent,
  trackDealerPerformanceEvent,
} from "../../../constants/automapDealerPerformanceConstants";
import { useDealerPortalIntl } from "../../../hooks/useDealerPortalIntl";
import {
  exportDealerPerformanceReport,
  fetchDealerPerformanceSummary,
  postDealerPerformanceEvent,
} from "../../../services/automapDealerPerformanceApi";
import { fetchDealerFeaturedPortal } from "../../../services/automapFeaturedPlacementApi";
import { fetchDealerTrialPanel } from "../../../services/automapDealerTrialApi";
import {
  DealerReportsNav,
  RecommendedActions,
  ReportPageHeader,
  ReportSummaryGrid,
  SimpleTrendBars,
} from "./DealerPerformanceShared";

export default function DealerReportsHubPage() {
  const { dealerId } = useOutletContext();
  const { t, translateError, translateTrialMessage } = useDealerPortalIntl();
  const [range, setRange] = useState(DEFAULT_DEALER_REPORT_RANGE);
  const [payload, setPayload] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    trackDealerPerformanceEvent("dealer_report_dashboard_viewed", { dealerId, range });
    postDealerPerformanceEvent(dealerId, "dealer_report_dashboard_viewed", { range }).catch(() => {});
  }, [dealerId, range]);

  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      fetchDealerPerformanceSummary(dealerId, range),
      fetchDealerTrialPanel(dealerId),
      fetchDealerFeaturedPortal(dealerId),
    ])
      .then(([summaryData, trialRes, featuredRes]) => {
        setPayload(summaryData);
        const panel = trialRes?.panel || null;
        setTrial(panel ? { ...panel, message: translateTrialMessage(panel) } : null);
        setFeatured(featuredRes?.featured || null);
      })
      .catch((err) => setError(translateError(err.message, "loadReports")))
      .finally(() => setLoading(false));
  }, [dealerId, range, translateError, translateTrialMessage]);

  const s = payload?.summary;

  return (
    <div className="dealer-reports">
      <DealerReportsNav active="hub" />
      <ReportPageHeader
        title={t("reports.hubTitle")}
        description={t("reports.hubDescription")}
        dealerId={dealerId}
        range={range}
        onRangeChange={setRange}
        onExport={exportDealerPerformanceReport}
      />
      {loading ? <p className="dealer-state-box">{t("reports.loading")}</p> : null}
      {error ? <p className="dealer-state-box">{error}</p> : null}
      {!loading && !error && s ? (
        <>
          <ReportSummaryGrid
            items={[
              { label: t("reports.listingViews"), value: s.totalListingViews },
              { label: t("reports.whatsappClicks"), value: s.totalWhatsappClicks },
              { label: t("reports.leadsReceived"), value: s.totalLeads },
              { label: t("reports.qualifiedLeads"), value: s.qualifiedLeads },
              { label: t("reports.activeInventory"), value: s.activeListingCount },
              { label: t("reports.inventoryFreshness"), value: formatPercent(s.inventoryFreshnessPercent) },
              {
                label: t("reports.avgResponseTime"),
                value:
                  s.averageResponseTimeMinutes != null
                    ? `${s.averageResponseTimeMinutes}${t("common.minSuffix")}`
                    : t("reports.insufficientResponseData"),
              },
              {
                label: t("reports.featuredPlacement"),
                value: s.topListing?.title || "—",
                hint: s.topListing ? t("common.viewsCount", { count: s.topListing.views }) : undefined,
              },
            ]}
          />

          {featured ? (
            <section className="dealer-state-box">
              <h3>{t("reports.featuredPlacement")}</h3>
              <p className="muted">{t("reports.featuredIntro")}</p>
              <div className="dashboard-lead-stats">
                <span>
                  {t("reports.impressions")}: {featured.performance?.impressions ?? 0}
                </span>
                <span>
                  {t("reports.clicks")}: {featured.performance?.clicks ?? 0}
                </span>
                <span>
                  {t("common.whatsapp")}: {featured.performance?.whatsappClicks ?? 0}
                </span>
                <span>
                  {t("reports.leadsReceived")}: {featured.performance?.leads ?? 0}
                </span>
                <span>
                  {t("reports.topFeatured")}: {featured.topFeaturedListing || "—"}
                </span>
              </div>
            </section>
          ) : null}

          {trial?.trialStatus && trial.trialStatus !== "not_started" ? (
            <section className="dealer-state-box">
              <h3>{t("reports.trialPerformance")}</h3>
              <p className="muted">{trial.message}</p>
              <div className="dashboard-lead-stats">
                <span>
                  {t("reports.daysRemaining")}: {trial.trialDaysRemaining ?? "—"}
                </span>
                <span>
                  {t("reports.leadsReceived")}: {trial.leadsReceived ?? 0}
                </span>
                <span>
                  {t("reports.views")}: {trial.views ?? 0}
                </span>
                <span>
                  {t("reports.whatsappClicks")}: {trial.whatsappClicks ?? 0}
                </span>
                <span>
                  {t("reports.inventoryFreshnessPct")}: {trial.inventoryFreshnessPercent ?? "—"}%
                </span>
              </div>
              {(trial.suggestedActions || []).slice(0, 3).map((action) => (
                <p key={action} className="muted">
                  {action}
                </p>
              ))}
              <div className="import-actions">
                <Link className="dealer-portal-btn" to="/v/automap/dealer/leads">
                  {t("reports.respondLeads")}
                </Link>
                <Link className="dealer-portal-btn" to="/v/automap/dealer/inventario">
                  {t("reports.updateInventory")}
                </Link>
              </div>
            </section>
          ) : null}

          <div className="dashboard-main-grid">
            <SimpleTrendBars title={t("reports.viewsOverTime")} points={payload.trends?.views} />
            <SimpleTrendBars title={t("reports.whatsappOverTime")} points={payload.trends?.whatsappClicks} />
            <SimpleTrendBars title={t("reports.leadsOverTime")} points={payload.trends?.leads} />
          </div>

          <RecommendedActions actions={payload.recommendedActions} />

          <section className="dealer-state-box">
            <h3>{t("reports.exploreReports")}</h3>
            <div className="import-actions">
              <Link className="dealer-portal-btn primary" to="/v/automap/dealer/reportes/rendimiento">
                {t("reports.listingPerformance")}
              </Link>
              <Link className="dealer-portal-btn" to="/v/automap/dealer/reportes/leads">
                {t("reports.leadPerformance")}
              </Link>
              <Link className="dealer-portal-btn" to="/v/automap/dealer/reportes/inventario">
                {t("reports.inventoryReport")}
              </Link>
              <Link className="dealer-portal-btn" to="/v/automap/dealer/reportes/fuentes">
                {t("reports.sourcesReport")}
              </Link>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
